import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserCheck, ScanBarcode, X, Split, AlertTriangle, Package, ThumbsUp, ShieldAlert, Lock, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const currentUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};
const isAdminRole = () => {
  const r = (currentUser().role || localStorage.getItem('role') || '').toString().trim().toLowerCase();
  return ['admin', 'super_admin', 'site admin', 'site_admin'].includes(r);
};

const Segregation = () => {
  const navigate = useNavigate();
  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);
  const usnInputRef = useRef(null);

  // Step: 'staff' | 'work'
  const [step, setStep] = useState('staff');

  // Multi-staff session
  const [staffValue, setStaffValue] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [validating, setValidating] = useState(false);

  // Bundle (LATE BIND: expected_qty + already-bound pool USNs)
  const [bundleValue, setBundleValue] = useState('');
  const [loadingBundle, setLoadingBundle] = useState(false);
  const [bundle, setBundle] = useState(null);

  // Scanning pool stickers
  const [activeTab, setActiveTab] = useState('GOOD');
  const [usnValue, setUsnValue] = useState('');
  const [scanning, setScanning] = useState(false);
  const [goodScanned, setGoodScanned] = useState([]);   // newly scanned this session
  const [damageScanned, setDamageScanned] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [lastSubmitted, setLastSubmitted] = useState(null);

  // Admin override (count mismatch)
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [mismatch, setMismatch] = useState(null); // { expected, scanned, admin, message }

  const focusUsn = useCallback(() => setTimeout(() => usnInputRef.current?.focus(), 80), []);
  const focusBundle = useCallback(() => setTimeout(() => bundleInputRef.current?.focus(), 80), []);

  // ----- Derived -----
  const boundMap = bundle?.bound || {};
  const boundGood = useMemo(() => Object.keys(boundMap).filter((u) => boundMap[u] === 'GOOD'), [boundMap]);
  const boundDamage = useMemo(() => Object.keys(boundMap).filter((u) => boundMap[u] === 'DAMAGE'), [boundMap]);
  const boundSet = useMemo(() => new Set(Object.keys(boundMap)), [boundMap]);

  const goodAll = useMemo(() => [...boundGood, ...goodScanned], [boundGood, goodScanned]);
  const damageAll = useMemo(() => [...boundDamage, ...damageScanned], [boundDamage, damageScanned]);
  const goodAllSet = useMemo(() => new Set(goodAll), [goodAll]);
  const damageAllSet = useMemo(() => new Set(damageAll), [damageAll]);

  const scannedCount = goodAll.length + damageAll.length;
  const expected = bundle?.expected_qty || 0;
  const hasNewScans = goodScanned.length + damageScanned.length > 0;
  const matches = scannedCount === expected;
  const canSubmit = hasNewScans && !submitting;

  // ---------- STAFF ----------
  const handleStaffAdd = async (e) => {
    e.preventDefault();
    const id = staffValue.trim();
    if (!id || validating) return;
    if (staffList.some((s) => s.staff_id === id)) {
      toast.error(`${id} already added`);
      setStaffValue('');
      return;
    }
    setValidating(true);
    try {
      const resp = await apiClient.post('/segregation/validate-staff', { staff_id: id });
      setStaffList((prev) => [...prev, { staff_id: resp.data.staff_id, staff_name: resp.data.staff_name }]);
      setStaffValue('');
      toast.success(`Added ${resp.data.staff_name}`);
      setTimeout(() => staffInputRef.current?.focus(), 60);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid staff ID');
      setStaffValue('');
    } finally {
      setValidating(false);
    }
  };

  const handleRemoveStaff = (id) => setStaffList((prev) => prev.filter((s) => s.staff_id !== id));

  const handleStartSession = () => {
    if (staffList.length === 0) {
      toast.error('Add at least one staff member');
      return;
    }
    setStep('work');
    focusBundle();
  };

  // ---------- BUNDLE ----------
  const resetBundle = () => {
    setBundle(null);
    setBundleValue('');
    setGoodScanned([]);
    setDamageScanned([]);
    setActiveTab('GOOD');
    setUsnValue('');
    focusBundle();
  };

  const handleLoadBundle = async (e) => {
    e?.preventDefault?.();
    const code = bundleValue.trim();
    if (!code || loadingBundle) return;
    setLoadingBundle(true);
    try {
      const resp = await apiClient.get(`/segregation/bundle/${encodeURIComponent(code)}`);
      setBundle(resp.data);
      setGoodScanned([]);
      setDamageScanned([]);
      setActiveTab('GOOD');
      const done = resp.data.bound_count || 0;
      if (done >= (resp.data.expected_qty || 0) && (resp.data.expected_qty || 0) > 0) {
        toast.warning(`Bundle already fully segregated (${done}/${resp.data.expected_qty}).`);
      } else if (done > 0) {
        toast.message(`${done} of ${resp.data.expected_qty} already bound — scan the rest.`);
      }
      focusUsn();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bundle not found or not segregatable');
      setBundle(null);
    } finally {
      setLoadingBundle(false);
    }
  };

  // ---------- SCAN (bind a pre-printed pool sticker) ----------
  const handleUsnScan = async (e) => {
    e.preventDefault();
    const code = usnValue.trim();
    if (!code || scanning) return;
    if (boundSet.has(code)) {
      toast.error(`USN ${code} already saved as ${boundMap[code]}`);
      setUsnValue(''); focusUsn(); return;
    }
    if (goodAllSet.has(code) || damageAllSet.has(code)) {
      toast.error(`USN ${code} already scanned`);
      setUsnValue(''); focusUsn(); return;
    }
    setScanning(true);
    try {
      const resp = await apiClient.post('/segregation/validate-pool-usn', { usn_code: code, bundle_code: bundle.bundle_code });
      if (resp.data.state === 'already_bound_here') {
        toast.error(`USN ${code} already saved as ${resp.data.result}`);
      } else {
        if (activeTab === 'GOOD') setGoodScanned((prev) => [code, ...prev]);
        else setDamageScanned((prev) => [code, ...prev]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `USN ${code} is not a valid pool sticker`);
    } finally {
      setScanning(false);
      setUsnValue('');
      focusUsn();
    }
  };

  const removeScanned = (code) => {
    setGoodScanned((prev) => prev.filter((c) => c !== code));
    setDamageScanned((prev) => prev.filter((c) => c !== code));
  };

  // ---------- SUBMIT ----------
  const doSubmit = async (reason = '') => {
    setSubmitting(true);
    try {
      const resp = await apiClient.post('/segregation/submit-bundle', {
        bundle_code: bundle.bundle_code,
        good_usns: goodScanned,
        damage_usns: damageScanned,
        staff_ids: staffList.map((s) => s.staff_id),
        override_reason: reason,
      });
      setLastSubmitted({
        bundle_code: resp.data.bundle_code,
        good_total: resp.data.good_total,
        damage_total: resp.data.damage_total,
        usn_count: resp.data.usn_count,
        override_used: resp.data.override_used,
      });
      toast.success(`Bundle ${resp.data.bundle_code} segregated — ${resp.data.good_total} good · ${resp.data.damage_total} damage${resp.data.override_used ? ' (override)' : ''}`);
      setOverrideOpen(false);
      setOverrideReason('');
      setMismatch(null);
      resetBundle();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && detail.code === 'COUNT_MISMATCH') {
        // Open the admin-override prompt (or show the not-admin message).
        setMismatch(detail);
        setOverrideOpen(true);
        if (reason) toast.error(detail.message || 'Count mismatch — override not accepted');
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Submit failed — please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    // If the count already differs, go straight to the override prompt.
    if (!matches) {
      setMismatch({ expected, scanned: scannedCount, admin: isAdminRole(),
        message: `Scanned ${scannedCount} piece(s) but Processing Inward expected ${expected}.` });
      setOverrideOpen(true);
      return;
    }
    void doSubmit('');
  };

  const isGood = activeTab === 'GOOD';
  const currentLocked = isGood ? boundGood : boundDamage;
  const currentScanned = isGood ? goodScanned : damageScanned;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="segregation-page">
      {/* Header */}
      <div className="bg-white border-b px-4 pt-12 pb-3 sm:pt-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900">Segregation</h1>
          <p className="text-xs text-gray-500 truncate">Scan a pre-printed USN sticker onto every piece &amp; split Good / Damage</p>
        </div>
        {step === 'work' && staffList.length > 0 && (
          <div className="hidden sm:flex items-center gap-1.5 bg-lime-50 px-3 py-1.5 rounded-lg" data-testid="staff-badge">
            <UserCheck className="w-4 h-4 text-lime-600" />
            <span className="text-sm font-medium text-lime-800">{staffList.length} staff</span>
          </div>
        )}
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">

        {/* ============ STEP: STAFF ============ */}
        {step === 'staff' && (
          <>
            <Card className="border-lime-200">
              <CardHeader className="text-center pb-2">
                <Split className="w-10 h-10 text-lime-600 mx-auto mb-2" />
                <CardTitle className="text-base">Scan Staff IDs</CardTitle>
                <p className="text-sm text-gray-500">Add one or more staff, then start segregating</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleStaffAdd} className="flex gap-2">
                  <Input
                    ref={staffInputRef}
                    value={staffValue}
                    onChange={(e) => setStaffValue(e.target.value)}
                    placeholder="Staff ID (e.g., V0104)"
                    autoFocus
                    data-testid="staff-input"
                  />
                  <Button type="submit" disabled={validating || !staffValue.trim()} data-testid="staff-add-btn">
                    {validating ? '...' : 'Add'}
                  </Button>
                </form>

                {staffList.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {staffList.map((s) => (
                      <Badge key={s.staff_id} className="bg-lime-100 text-lime-800 border-lime-200 pl-2.5 pr-1 py-1 flex items-center gap-1" data-testid={`staff-chip-${s.staff_id}`}>
                        <span className="text-xs">{s.staff_name} ({s.staff_id})</span>
                        <button onClick={() => handleRemoveStaff(s.staff_id)} className="hover:text-red-600" data-testid={`staff-remove-${s.staff_id}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                <Button
                  className="w-full mt-4 bg-lime-600 hover:bg-lime-700"
                  onClick={handleStartSession}
                  disabled={staffList.length === 0}
                  data-testid="start-session-btn"
                >
                  Start Segregation ({staffList.length})
                </Button>
              </CardContent>
            </Card>

            {lastSubmitted && (
              <Card className="border-green-200 bg-green-50/40" data-testid="last-submitted-card">
                <CardContent className="pt-4 text-sm text-green-800">
                  Last bundle <span className="font-mono font-semibold">{lastSubmitted.bundle_code}</span> · {lastSubmitted.good_total} good · {lastSubmitted.damage_total} damage
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ============ STEP: WORK ============ */}
        {step === 'work' && !bundle && (
          <Card className="border-lime-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4" /> Scan Bundle</CardTitle>
              <p className="text-sm text-gray-500">Scan or type the bundle code to load it</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLoadBundle} className="flex gap-2">
                <Input
                  ref={bundleInputRef}
                  value={bundleValue}
                  onChange={(e) => setBundleValue(e.target.value)}
                  placeholder="e.g., BUN042253/25-26"
                  autoFocus
                  disabled={loadingBundle}
                  data-testid="bundle-scan-input"
                />
                <Button type="submit" disabled={loadingBundle || !bundleValue.trim()} data-testid="load-bundle-btn">
                  {loadingBundle ? '...' : <ScanBarcode className="w-4 h-4" />}
                </Button>
              </form>
              {lastSubmitted && (
                <p className="text-xs text-green-700 mt-3" data-testid="last-submitted-inline">
                  ✓ {lastSubmitted.bundle_code} submitted · {lastSubmitted.good_total} good · {lastSubmitted.damage_total} damage
                </p>
              )}
              <Button variant="outline" className="w-full mt-3" onClick={() => setStep('staff')} data-testid="finish-session-btn">
                Finish Session
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 'work' && bundle && (
          <>
            {/* Bundle info + progress */}
            <Card className="border-lime-200">
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono font-bold text-gray-900 truncate" data-testid="active-bundle-code">{bundle.bundle_code}</p>
                    <p className="text-xs text-gray-500 truncate">{bundle.design_name || '-'} · {bundle.shade || '-'} · {bundle.sku_code || '-'}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetBundle}
                    data-testid="change-bundle-btn"
                    className="text-gray-500 shrink-0"
                  >
                    <X className="w-4 h-4 mr-1" /> Change
                  </Button>
                </div>
                <div className="mt-3">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">Scanned</span>
                    <span className="font-semibold" data-testid="scan-progress">{scannedCount} / {expected}</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${matches ? 'bg-green-500' : 'bg-lime-500'} transition-all`}
                      style={{ width: `${expected ? Math.min(100, Math.round((scannedCount / expected) * 100)) : 0}%` }}
                    />
                  </div>
                  {!matches && scannedCount > 0 && (
                    <p className="text-xs text-amber-700 mt-1.5 flex items-center gap-1" data-testid="count-mismatch-hint">
                      <AlertTriangle className="w-3 h-3" />
                      {scannedCount > expected
                        ? `${scannedCount - expected} more than expected — Admin override needed`
                        : `${expected - scannedCount} short of expected — Admin override needed`}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Tabs */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={isGood ? 'default' : 'outline'}
                className={isGood ? 'bg-green-600 hover:bg-green-700' : ''}
                onClick={() => { setActiveTab('GOOD'); focusUsn(); }}
                data-testid="tab-good"
              >
                <ThumbsUp className="w-4 h-4 mr-2" /> Good ({goodAll.length})
              </Button>
              <Button
                variant={!isGood ? 'default' : 'outline'}
                className={!isGood ? 'bg-amber-600 hover:bg-amber-700' : ''}
                onClick={() => { setActiveTab('DAMAGE'); focusUsn(); }}
                data-testid="tab-damage"
              >
                <ShieldAlert className="w-4 h-4 mr-2" /> Damage ({damageAll.length})
              </Button>
            </div>

            {/* Scan input */}
            <Card className={isGood ? 'border-green-200' : 'border-amber-200'}>
              <CardContent className="pt-4">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Scan pre-printed USN sticker into <span className={isGood ? 'text-green-700 font-bold' : 'text-amber-700 font-bold'}>{activeTab}</span>
                </label>
                <form onSubmit={handleUsnScan} className="flex gap-2">
                  <Input
                    ref={usnInputRef}
                    value={usnValue}
                    onChange={(e) => setUsnValue(e.target.value)}
                    placeholder="Scan USN barcode..."
                    autoFocus
                    disabled={scanning}
                    data-testid="usn-scan-input"
                  />
                  <Button type="submit" disabled={!usnValue.trim() || scanning} data-testid="usn-scan-submit">
                    <ScanBarcode className="w-4 h-4" />
                  </Button>
                </form>
                <p className="text-xs text-gray-400 mt-1.5">Expected {expected} piece{expected !== 1 ? 's' : ''} · scanned {scannedCount}.</p>
              </CardContent>
            </Card>

            {/* Current tab list */}
            {(currentScanned.length > 0 || currentLocked.length > 0) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Package className="w-4 h-4" /> {activeTab} ({currentScanned.length + currentLocked.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1.5 max-h-72 overflow-y-auto">
                    {currentScanned.map((code) => (
                      <div key={code} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm" data-testid={`queued-usn-${code}`}>
                        <div className="flex items-center gap-2">
                          <ScanBarcode className={`w-3.5 h-3.5 ${isGood ? 'text-green-500' : 'text-amber-500'}`} />
                          <span className="font-mono font-medium">{code}</span>
                        </div>
                        <Button variant="ghost" size="sm" className="text-gray-400 hover:text-red-600" onClick={() => removeScanned(code)} data-testid={`queued-delete-${code}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {currentLocked.map((code) => (
                      <div key={code} className="flex items-center justify-between bg-gray-100 rounded px-3 py-1.5 text-sm opacity-80" data-testid={`locked-usn-${code}`}>
                        <div className="flex items-center gap-2">
                          <Lock className="w-3.5 h-3.5 text-gray-400" />
                          <span className="font-mono font-medium text-gray-600">{code}</span>
                        </div>
                        <span className="text-xs text-gray-400">already saved</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Submit */}
            <Button
              className="w-full bg-lime-600 hover:bg-lime-700"
              size="lg"
              onClick={handleSubmit}
              disabled={!canSubmit}
              data-testid="submit-bundle-btn"
            >
              {submitting ? 'Submitting...' : (
                <><Split className="w-4 h-4 mr-2" /> Submit Bundle — {goodAll.length} good · {damageAll.length} damage</>
              )}
            </Button>
            {!matches && hasNewScans && (
              <p className="text-xs text-amber-700 text-center flex items-center justify-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Count differs from expected ({scannedCount}/{expected}) — submit will ask for an Admin override.
              </p>
            )}
          </>
        )}
      </div>

      {/* ===== Admin override dialog (count mismatch) ===== */}
      <Dialog open={overrideOpen} onOpenChange={(o) => { setOverrideOpen(o); if (!o) setOverrideReason(''); }}>
        <DialogContent data-testid="seg-override-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-amber-600" /> Count mismatch — Admin override
            </DialogTitle>
            <DialogDescription>
              {mismatch?.message || `Scanned ${scannedCount} but expected ${expected}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex gap-3 text-sm">
              <span className="px-2 py-1 rounded bg-gray-100" data-testid="seg-override-expected">Expected: <b>{mismatch?.expected ?? expected}</b></span>
              <span className="px-2 py-1 rounded bg-gray-100" data-testid="seg-override-scanned">Scanned: <b>{mismatch?.scanned ?? scannedCount}</b></span>
            </div>
            {isAdminRole() ? (
              <>
                <label className="text-sm font-medium text-gray-700">Reason (required)</label>
                <Textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Why does the physical count differ from the expected quantity?"
                  rows={3}
                  data-testid="seg-override-reason"
                />
              </>
            ) : (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="seg-override-not-admin">
                An Admin must approve this mismatch. Ask an Admin to submit, or correct the scanned count.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOverrideOpen(false); setOverrideReason(''); }} data-testid="seg-override-cancel">Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              disabled={!isAdminRole() || !overrideReason.trim() || submitting}
              onClick={() => doSubmit(overrideReason.trim())}
              data-testid="seg-override-confirm"
            >
              {submitting ? 'Submitting…' : 'Approve & Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Segregation;
